import { useEffect, useMemo, useState } from "react";
import {
  Sun,
  Snowflake,
  CloudRain,
  CloudSnow,
  ArrowUpRight,
  Trophy,
  MapPin,
  Cloud,
  CloudLightning,
  Moon,
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { getIndiaExtremes } from "@/lib/api";

const TABS = [
  {
    id: "hot",
    label: "Hottest",
    Icon: Sun,
    tone: "#f59e0b",
    toneSoft: "rgba(245,158,11,0.16)",
    heroFrom: "#fde68a",
    heroVia: "#fbbf24",
    heroTo: "#f97316",
    accent: "Hottest",
    metricLabel: "°C",
    metricKey: "temperature_c",
  },
  {
    id: "cold",
    label: "Coldest",
    Icon: Snowflake,
    tone: "#0284c7",
    toneSoft: "rgba(2,132,199,0.16)",
    heroFrom: "#bae6fd",
    heroVia: "#7dd3fc",
    heroTo: "#0ea5e9",
    accent: "Coldest",
    metricLabel: "°C",
    metricKey: "temperature_c",
  },
  {
    id: "wet",
    label: "Wettest",
    Icon: CloudRain,
    tone: "#4f46e5",
    toneSoft: "rgba(79,70,229,0.16)",
    heroFrom: "#c7d2fe",
    heroVia: "#818cf8",
    heroTo: "#4f46e5",
    accent: "Wettest",
    metricLabel: "mm/6h",
    metricKey: "next_6h_precip_mm",
  },
  {
    id: "snow",
    label: "Snowing",
    Icon: CloudSnow,
    // Frosty cyan/sky palette — visually distinct from Coldest's deep blue
    // (cold = "below zero air", snow = "active flake-fall"), so a user
    // glancing the tab bar can tell the two apart instantly.
    tone: "#0e7490",
    toneSoft: "rgba(14,116,144,0.14)",
    heroFrom: "#e0f2fe",
    heroVia: "#67e8f9",
    heroTo: "#0e7490",
    accent: "Snowing",
    metricLabel: "cm/6h",
    metricKey: "next_6h_snow_cm",
  },
];

// Same weather-code → icon mapping used elsewhere
function CityIcon({ code, isDay = true, size = 24, color }) {
  if (code == null) return null;
  if (code >= 95) return <CloudLightning size={size} weight="fill" color={color} />;
  if (code >= 51) return <CloudRain size={size} weight="duotone" color={color} />;
  if (code === 3 || (code >= 45 && code <= 48)) return <Cloud size={size} weight="duotone" color={color} />;
  if (code === 0 && !isDay) return <Moon size={size} weight="duotone" color={color} />;
  return <Sun size={size} weight="fill" color={color} />;
}

/**
 * ExtremesPage v2 — podium-style layout. Hero #1 city dominates the top with
 * a gradient banner + weather icon + huge metric. Ranks #2-#3 sit side-by-side
 * as silver/bronze cards. Ranks #4-#8 form a compact 2-col list below.
 *
 * Tap any card → switches the active app location to that city (jumps to
 * Page 1 via parent's `onSelectCity`).
 */
export default function ExtremesPage({ theme, onSelectCity, refreshNonce = 0, location }) {
  const [activeTab, setActiveTab] = useState("hot");
  const [cache, setCache] = useState({});
  const [loading, setLoading] = useState(false);

  // When the user taps the masthead refresh button, the parent bumps
  // `refreshNonce`. We wipe our internal cache so every tab refetches
  // fresh data on its next view (was previously stuck on first-load
  // results until the user reopened the app).
  useEffect(() => {
    if (refreshNonce > 0) {
      setCache({});
    }
  }, [refreshNonce]);

  useEffect(() => {
    if (cache[activeTab]) return;
    let cancelled = false;
    setLoading(true);
    // Cache-bust the request whenever it's triggered by a refreshNonce
    // bump so the browser + service worker pull truly fresh data.
    getIndiaExtremes(activeTab, 12, { bustCache: refreshNonce > 0 })
      .then((res) => !cancelled && setCache((c) => ({ ...c, [activeTab]: res })))
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [activeTab, cache, refreshNonce]);

  const tab = TABS.find((t) => t.id === activeTab);
  const cities = cache[activeTab]?.cities || [];
  const champion = cities[0];
  const runnersUp = cities.slice(1, 3);
  const rest = cities.slice(3, 12);

  const darkMood = theme.id === "night" || theme.id === "storm";

  // National "spread" stat for context — e.g. 41°C ↔ 12°C
  const spread = useMemo(() => {
    if (!cities.length || tab.id === "wet" || tab.id === "snow") return null;
    const vals = cities.map((c) => c[tab.metricKey]).filter((v) => v != null);
    if (vals.length < 2) return null;
    return { min: Math.min(...vals), max: Math.max(...vals) };
  }, [cities, tab]);

  return (
    <div
      className="h-full w-full flex flex-col px-5 pt-2 pb-20"
      data-testid="page-extremes"
      style={{ color: theme.textPrimary }}
    >
      {/* Header */}
      <div className="shrink-0">
        <div className="flex items-baseline gap-2 mb-1">
          <Trophy size={11} weight="duotone" style={{ color: tab.tone }} />
          <span
            className="text-[10px] uppercase tracking-[0.2em] font-semibold"
            style={{ color: theme.textMuted }}
          >
            Across India · live ranking
          </span>
        </div>
        <h2
          className="font-display text-[20px] sm:text-[22px] font-semibold tracking-tight leading-snug"
          style={{ color: theme.textPrimary }}
          data-testid="extremes-headline"
        >
          {tab.accent} cities right now
        </h2>
        {spread && (
          <p
            className="text-[11px] mt-0.5"
            style={{ color: theme.textMuted }}
            data-testid="extremes-spread"
          >
            India spread today:{" "}
            <span className="font-semibold tabular-nums" style={{ color: theme.textSecondary }}>
              {Math.round(spread.max)}° → {Math.round(spread.min)}°
            </span>
          </p>
        )}

        {/* Tab pills */}
        <div className="mt-3 flex gap-1.5" role="tablist" data-testid="extremes-tabs">
          {TABS.map((t) => {
            const isActive = t.id === activeTab;
            const Icon = t.Icon;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                data-testid={`extremes-tab-${t.id}`}
                onClick={() => setActiveTab(t.id)}
                className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-full text-[11px] font-semibold transition-all"
                style={{
                  background: isActive ? t.tone : theme.chip,
                  color: isActive ? "#fff" : theme.textSecondary,
                  border: `1px solid ${isActive ? t.tone : theme.surfaceBorder}`,
                  boxShadow: isActive ? `0 4px 14px ${t.tone}55` : "none",
                }}
              >
                <Icon size={11} weight={isActive ? "fill" : "duotone"} />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 mt-3 overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            className="h-full flex flex-col gap-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.32 }}
          >
            {loading && !champion ? (
              <div className="flex flex-col gap-2 h-full">
                <div
                  className="rounded-2xl animate-pulse"
                  style={{ background: theme.surface, height: 140 }}
                />
                <div className="grid grid-cols-2 gap-2">
                  {Array.from({ length: 2 }).map((_, i) => (
                    <div
                      key={i}
                      className="rounded-2xl animate-pulse"
                      style={{ background: theme.surface, height: 90 }}
                    />
                  ))}
                </div>
                <div className="flex-1 grid grid-cols-2 gap-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div
                      key={i}
                      className="rounded-2xl animate-pulse"
                      style={{ background: theme.surface }}
                    />
                  ))}
                </div>
              </div>
            ) : !champion ? (
              // Empty-state — happens for the Snowing tab during summer when
              // nowhere in India has active snowfall. The Wet tab can also
              // empty out during dry spells. Friendlier than showing a
              // blank screen.
              <div
                className="flex-1 flex flex-col items-center justify-center text-center px-6"
                data-testid="extremes-empty"
              >
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                  style={{ background: tab.toneSoft }}
                >
                  <tab.Icon size={32} weight="duotone" color={tab.tone} />
                </div>
                <p
                  className="text-[15px] font-semibold mb-1"
                  style={{ color: theme.textPrimary }}
                >
                  {tab.id === "snow"
                    ? "No active snowfall in India right now"
                    : "Nothing matches this filter just now"}
                </p>
                <p className="text-[12px]" style={{ color: theme.textMuted }}>
                  {tab.id === "snow"
                    ? "Snow is rare outside winter months. Check back from December–March when the Himalayas wake up."
                    : "Try another tab — Hottest, Coldest and Wettest always have a top 12 ready."}
                </p>
              </div>
            ) : (
              <>
                {/* Champion hero — full width #1 */}
                <ChampionCard
                  city={champion}
                  tab={tab}
                  onTap={() => onSelectCity(toLocation(champion))}
                />

                {/* Runners up — 2-col silver/bronze */}
                {runnersUp.length > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    {runnersUp.map((c, i) => (
                      <PodiumCard
                        key={c.name}
                        city={c}
                        rank={i + 2}
                        tab={tab}
                        theme={theme}
                        onTap={() => onSelectCity(toLocation(c))}
                      />
                    ))}
                  </div>
                )}

                {/* Rest — single-column list distributes vertically to fill
                    remaining space. `auto-rows-fr` makes every row equal-height,
                    so #4-#12 form a clean rhythm without truncation. */}
                {rest.length > 0 && (
                  <div
                    className="flex-1 min-h-0 grid grid-cols-1 gap-1 overflow-y-auto pr-1 -mr-1"
                    style={{
                      overscrollBehavior: "contain",
                      gridAutoRows: "minmax(0, 1fr)",
                    }}
                  >
                    {rest.map((c, i) => (
                      <CompactRow
                        key={c.name}
                        city={c}
                        rank={i + 4}
                        tab={tab}
                        theme={theme}
                        onTap={() => onSelectCity(toLocation(c))}
                      />
                    ))}
                  </div>
                )}              </>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

function toLocation(c) {
  return {
    name: c.name,
    admin1: c.state,
    country: "India",
    latitude: c.lat,
    longitude: c.lon,
  };
}

function aqiTone(aqi) {
  if (aqi == null) return null;
  if (aqi <= 50) return { color: "#10b981", label: "Good" };
  if (aqi <= 100) return { color: "#f59e0b", label: "Moderate" };
  if (aqi <= 150) return { color: "#f97316", label: "USG" };
  if (aqi <= 200) return { color: "#ef4444", label: "Unhealthy" };
  if (aqi <= 300) return { color: "#a855f7", label: "Very unhealthy" };
  return { color: "#7f1d1d", label: "Hazardous" };
}

function AqiPill({ aqi, dark = false, size = "sm" }) {
  if (aqi == null) return null;
  const tone = aqiTone(aqi);
  if (!tone) return null;
  const padding = size === "sm" ? "px-1.5 py-[1px]" : "px-2 py-0.5";
  const fontSize = size === "sm" ? "text-[9px]" : "text-[9.5px]";
  return (
    <span
      className={`inline-flex items-center gap-0.5 ${padding} rounded-full font-bold tabular-nums uppercase tracking-wider ${fontSize}`}
      style={{
        background: dark ? "rgba(15,23,42,0.18)" : `${tone.color}1f`,
        color: dark ? "#0f172a" : tone.color,
        border: dark ? "1px solid rgba(15,23,42,0.18)" : `1px solid ${tone.color}55`,
      }}
      title={`AQI ${aqi} · ${tone.label}`}
    >
      <span className="opacity-70 font-semibold">AQI</span>
      <span>{aqi}</span>
    </span>
  );
}

function ChampionCard({ city, tab, onTap }) {
  const value = city[tab.metricKey];
  const isWet = tab.id === "wet";
  return (
    <motion.button
      type="button"
      onClick={onTap}
      className="relative w-full rounded-3xl p-3 text-left overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${tab.heroFrom} 0%, ${tab.heroVia} 55%, ${tab.heroTo} 100%)`,
        boxShadow: `0 10px 24px ${tab.tone}40`,
      }}
      whileTap={{ scale: 0.985 }}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="extremes-champion"
    >
      {/* Decorative big icon top-right */}
      <div className="absolute top-2 right-2 pointer-events-none opacity-30">
        <CityIcon
          code={isWet ? 65 : city.weather_code}
          isDay={city.is_day}
          size={70}
          color="rgba(255,255,255,0.55)"
        />
      </div>

      <div className="relative z-10 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider"
              style={{
                background: "rgba(255,255,255,0.55)",
                color: "#0f172a",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
              }}
            >
              <Trophy size={9} weight="fill" />
              #1 {tab.accent}
            </span>
            <AqiPill aqi={city.us_aqi} dark size="md" />
          </div>

          <div
            className="font-display text-[20px] font-bold tracking-tight leading-tight truncate"
            style={{ color: "#0f172a" }}
          >
            {city.name}
          </div>
          <div className="flex items-center gap-1 text-[10.5px] font-medium" style={{ color: "rgba(15,23,42,0.65)" }}>
            <MapPin size={10} weight="fill" />
            <span className="truncate">{city.state}</span>
          </div>
        </div>

        <div className="shrink-0 flex flex-col items-end">
          <div className="flex items-baseline gap-0.5">
            <span
              className="font-display tabular-nums leading-none"
              style={{
                fontSize: 44,
                fontWeight: 800,
                color: "#0f172a",
                letterSpacing: "-0.03em",
              }}
            >
              {isWet ? value.toFixed(1) : Math.round(value)}
            </span>
            <span
              className="font-semibold"
              style={{ fontSize: 13, color: "rgba(15,23,42,0.7)" }}
            >
              {tab.metricLabel}
            </span>
          </div>
          {!isWet && city.feels_like_c != null && Math.abs(city.feels_like_c - value) >= 1.5 && (
            <span
              className="text-[10px] font-semibold tabular-nums mt-0.5"
              style={{ color: "rgba(15,23,42,0.65)" }}
            >
              feels {Math.round(city.feels_like_c)}°
            </span>
          )}
        </div>
      </div>

      <ArrowUpRight
        size={14}
        weight="bold"
        className="absolute bottom-2 right-2 opacity-60 pointer-events-none"
        style={{ color: "#0f172a" }}
      />
    </motion.button>
  );
}

function PodiumCard({ city, rank, tab, theme, onTap }) {
  const value = city[tab.metricKey];
  const isWet = tab.id === "wet";
  // Silver and bronze accent for rank 2/3
  const rankBg = rank === 2 ? "#cbd5e1" : "#f59e0b";

  return (
    <motion.button
      type="button"
      onClick={onTap}
      className="relative rounded-2xl p-2.5 text-left overflow-hidden"
      style={{
        background: theme.surface,
        border: `1px solid ${tab.tone}40`,
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
      }}
      whileTap={{ scale: 0.98 }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, delay: 0.08 * rank }}
      data-testid={`extremes-podium-${rank}`}
    >
      <div className="flex items-center gap-1.5 mb-1">
        <span
          className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold tabular-nums shrink-0"
          style={{ background: rankBg, color: "#0f172a" }}
        >
          {rank}
        </span>
        <CityIcon
          code={isWet ? 65 : city.weather_code}
          isDay={city.is_day}
          size={13}
          color={tab.tone}
        />
        {city.us_aqi != null && (
          <span className="ml-auto shrink-0">
            <AqiPill aqi={city.us_aqi} size="sm" />
          </span>
        )}
      </div>
      <div
        className="font-display text-[14px] font-semibold tracking-tight truncate leading-tight"
        style={{ color: theme.textPrimary }}
      >
        {city.name}
      </div>
      <div
        className="text-[10px] truncate mb-1"
        style={{ color: theme.textMuted }}
      >
        {city.state}
      </div>
      <div className="flex items-baseline gap-0.5">
        <span
          className="font-display tabular-nums leading-none"
          style={{ fontSize: 24, fontWeight: 700, color: tab.tone }}
        >
          {isWet ? value.toFixed(1) : Math.round(value)}
        </span>
        <span
          className="text-[10px] font-medium"
          style={{ color: theme.textMuted }}
        >
          {tab.metricLabel}
        </span>
      </div>
    </motion.button>
  );
}

function CompactRow({ city, rank, tab, theme, onTap }) {
  const value = city[tab.metricKey];
  const isWet = tab.id === "wet";
  return (
    <motion.button
      type="button"
      onClick={onTap}
      className="rounded-xl px-2.5 text-left flex items-center gap-2.5 min-h-0"
      style={{
        background: theme.chip,
        border: `1px solid ${theme.surfaceBorder}`,
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
      }}
      whileTap={{ scale: 0.98 }}
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.28, delay: 0.04 * rank }}
      data-testid={`extremes-row-${rank}`}
    >
      <span
        className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full text-[10.5px] font-bold tabular-nums"
        style={{ background: tab.toneSoft, color: tab.tone }}
      >
        {rank}
      </span>
      <CityIcon
        code={isWet ? 65 : city.weather_code}
        isDay={city.is_day}
        size={14}
        color={tab.tone}
      />
      <div className="flex-1 min-w-0">
        <div
          className="text-[13.5px] font-semibold truncate leading-tight"
          style={{ color: theme.textPrimary }}
        >
          {city.name}
        </div>
        <div
          className="text-[10px] truncate leading-tight"
          style={{ color: theme.textMuted }}
        >
          {city.state}
        </div>
      </div>
      {city.us_aqi != null && (
        <span className="shrink-0">
          <AqiPill aqi={city.us_aqi} size="sm" />
        </span>
      )}
      <div className="shrink-0 text-right min-w-[44px]">
        <span
          className="font-display tabular-nums leading-none"
          style={{ fontSize: 20, fontWeight: 700, color: tab.tone }}
        >
          {isWet ? value.toFixed(1) : Math.round(value)}
        </span>
        <span className="text-[10px] ml-0.5 font-medium" style={{ color: theme.textMuted }}>
          {isWet ? "mm" : "°"}
        </span>
      </div>
    </motion.button>
  );
}
